// src/views/pages/booking-engine/admin/PromoBannerManager.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Switch,
  Select,
  Drawer,
  Modal,
  Tag,
  Tooltip,
  Empty,
  Spin,
  Table,
  Badge,
} from "antd";



// Remove this:
// import { PlusOutlined, EditOutlined, ... } from "@ant-design/icons";

// Add this:
import {
  Plus as PlusOutlined,
  Edit2 as EditOutlined,
  Trash2 as DeleteOutlined,
  Eye as EyeOutlined,
  Copy as CopyOutlined,
  Image as PictureOutlined,
  Calendar as CalendarOutlined,
  CheckCircle as CheckCircleFilled,
  XCircle as ClockCircleOutlined,
  Clock as CloseCircleFilled,
} from "react-feather";


import axios from "axios";
import moment from "moment";
import InputColor from "react-input-color";
import { DateRangePicker } from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { useNotification } from "../../context/NotificationContext";

/* ══════════════════════════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════════════════════════ */

const SIZE_PRESETS = [
  { label: "Square (400 × 400)", width: 400, height: 400 },
  { label: "Leaderboard (728 × 90)", width: 728, height: 90 },
  { label: "Half-page (300 × 600)", width: 300, height: 600 },
  { label: "Wide Banner (970 × 250)", width: 970, height: 250 },
  { label: "Mobile Banner (320 × 480)", width: 320, height: 480 },
  { label: "Custom", width: 0, height: 0 },
];

const TEMPLATES = [
  { id: "gradient-bold", name: "Gradient Bold", desc: "Eye-catching gradient with large text & coupon pill" },
  { id: "minimal-elegant", name: "Minimal Elegant", desc: "Clean white card with accent border & shadows" },
];

const POSITIONS = [
  { value: "hero-overlay", label: "Hero Overlay" },
  { value: "below-hero", label: "Below Hero" },
  { value: "popup", label: "Popup (auto-show)" },
  { value: "inline", label: "Inline (in room listing)" },
];

const DEFAULT_FORM = {
  id: null,
  name: "",
  headline: "",
  subtext: "",
  coupon_code: "",
  cta_text: "Book Now",
  cta_url: "",
  template: "gradient-bold",
  bg_color_1: "#667eea",
  bg_color_2: "#764ba2",
  text_color: "#ffffff",
  accent_color: "#fbbf24",
  width: 400,
  height: 400,
  size_preset: "Square (400 × 400)",
  is_active: true,
  show_on_frontend: true,
  start_at: moment().format("YYYY-MM-DD"),
  end_at: moment().add(30, "days").format("YYYY-MM-DD"),
  position: "hero-overlay",
  custom_css: "",
  sort_order: 0,
};

/* ══════════════════════════════════════════════════════════
   COLOR PICKER WRAPPER (react-input-color)
   ══════════════════════════════════════════════════════════ */

const ColorField = ({ label, value, onChange }) => (
  <div style={{ minWidth: 100 }}>
    <div style={{ fontSize: 11, fontWeight: 800, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>
      {label}
    </div>
    <div className="d-flex align-items-center gap-2">
      <InputColor
        initialValue={value || "#000000"}
        onChange={(c) => {
          if (c?.hex && c.hex !== value) onChange(c.hex);
        }}
        placement="right"
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          border: "2px solid #e5e7eb",
          cursor: "pointer",
        }}
      />
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        style={{ width: 90, fontFamily: "monospace", fontSize: 12, fontWeight: 700, borderRadius: 8 }}
        placeholder="#000000"
      />
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   TEMPLATE RENDERERS
   ══════════════════════════════════════════════════════════ */

const GradientBoldTemplate = ({ banner, scale = 1 }) => {
  const w = banner.width || 400;
  const h = banner.height || 400;
  const isWide = w / h > 2;
  const isVert = h / w > 1.5;

  return (
    <div style={{
      width: w * scale, height: h * scale,
      background: `linear-gradient(135deg, ${banner.bg_color_1}, ${banner.bg_color_2})`,
      borderRadius: 16 * scale, padding: 20 * scale,
      display: "flex", flexDirection: isWide ? "row" : "column",
      alignItems: isWide ? "center" : "flex-start",
      justifyContent: isWide ? "space-between" : "center",
      gap: 10 * scale, position: "relative", overflow: "hidden",
      fontFamily: "Inter, system-ui, sans-serif",
      boxShadow: `0 ${20 * scale}px ${60 * scale}px rgba(0,0,0,0.25)`,
    }}>
      <div style={{ position: "absolute", top: -30 * scale, right: -30 * scale, width: 120 * scale, height: 120 * scale, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
      <div style={{ position: "absolute", bottom: -20 * scale, left: -20 * scale, width: 80 * scale, height: 80 * scale, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
      <div style={{ position: "relative", zIndex: 2, flex: 1 }}>
        {banner.coupon_code && (
          <div style={{
            display: "inline-block", background: banner.accent_color || "#fbbf24", color: "#111",
            fontWeight: 800, fontSize: Math.max(10, 13 * scale),
            padding: `${3 * scale}px ${12 * scale}px`, borderRadius: 999,
            letterSpacing: "0.08em", marginBottom: 10 * scale, textTransform: "uppercase",
          }}>USE CODE: {banner.coupon_code}</div>
        )}
        <div style={{
          color: banner.text_color || "#fff", fontWeight: 900,
          fontSize: Math.max(14, (isWide ? 22 : isVert ? 26 : 28) * scale),
          lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: 8 * scale,
        }}>{banner.headline || "Your Headline Here"}</div>
        {banner.subtext && (
          <div style={{
            color: banner.text_color || "#fff", opacity: 0.85,
            fontSize: Math.max(10, (isWide ? 12 : 14) * scale), lineHeight: 1.5,
            maxWidth: isWide ? 320 * scale : "100%", marginBottom: 12 * scale,
          }}>{banner.subtext}</div>
        )}
      </div>
      {banner.cta_text && (
        <div style={{
          position: "relative", zIndex: 2, background: "#fff",
          color: banner.bg_color_1 || "#667eea", fontWeight: 800,
          fontSize: Math.max(11, 14 * scale), padding: `${10 * scale}px ${24 * scale}px`,
          borderRadius: 999, boxShadow: `0 ${4 * scale}px ${16 * scale}px rgba(0,0,0,0.2)`,
          whiteSpace: "nowrap",
        }}>{banner.cta_text}</div>
      )}
    </div>
  );
};

const MinimalElegantTemplate = ({ banner, scale = 1 }) => {
  const w = banner.width || 400;
  const h = banner.height || 400;
  const isWide = w / h > 2;

  return (
    <div style={{
      width: w * scale, height: h * scale, background: "#ffffff",
      borderRadius: 16 * scale, border: `2px solid ${banner.bg_color_1 || "#667eea"}`,
      padding: 24 * scale, display: "flex", flexDirection: isWide ? "row" : "column",
      alignItems: isWide ? "center" : "flex-start", justifyContent: "center",
      gap: 12 * scale, position: "relative", overflow: "hidden",
      fontFamily: "Inter, system-ui, sans-serif",
      boxShadow: `0 ${8 * scale}px ${32 * scale}px rgba(0,0,0,0.08)`,
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4 * scale, background: `linear-gradient(90deg, ${banner.bg_color_1}, ${banner.bg_color_2 || banner.bg_color_1})` }} />
      <div style={{ flex: 1, zIndex: 2 }}>
        {banner.coupon_code && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6 * scale,
            border: `1.5px dashed ${banner.bg_color_1 || "#667eea"}`,
            borderRadius: 8 * scale, padding: `${4 * scale}px ${12 * scale}px`, marginBottom: 12 * scale,
          }}>
            <span style={{ fontSize: 14 * scale }}>🎟️</span>
            <span style={{ fontWeight: 800, fontSize: Math.max(10, 13 * scale), color: banner.bg_color_1 || "#667eea", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {banner.coupon_code}
            </span>
          </div>
        )}
        <div style={{ color: "#111827", fontWeight: 800, fontSize: Math.max(14, (isWide ? 20 : 24) * scale), lineHeight: 1.2, marginBottom: 6 * scale }}>
          {banner.headline || "Your Headline Here"}
        </div>
        {banner.subtext && (
          <div style={{ color: "#6b7280", fontSize: Math.max(10, 13 * scale), lineHeight: 1.5, marginBottom: 14 * scale }}>
            {banner.subtext}
          </div>
        )}
      </div>
      {banner.cta_text && (
        <div style={{
          zIndex: 2, background: `linear-gradient(135deg, ${banner.bg_color_1}, ${banner.bg_color_2 || banner.bg_color_1})`,
          color: "#fff", fontWeight: 700, fontSize: Math.max(11, 13 * scale),
          padding: `${9 * scale}px ${22 * scale}px`, borderRadius: 10 * scale,
          boxShadow: `0 ${4 * scale}px ${16 * scale}px ${banner.bg_color_1}33`, whiteSpace: "nowrap",
        }}>{banner.cta_text}</div>
      )}
    </div>
  );
};

const renderTemplate = (banner, scale = 1) => {
  if (banner.template === "minimal-elegant") return <MinimalElegantTemplate banner={banner} scale={scale} />;
  return <GradientBoldTemplate banner={banner} scale={scale} />;
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════ */

const PromoBannerManager = ({ offers = [] }) => {
  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const token = localStorage.getItem("token");
  const { notify } = useNotification();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [dateRange, setDateRange] = useState({
    startDate: moment(DEFAULT_FORM.start_at),
    endDate: moment(DEFAULT_FORM.end_at),
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBanner, setPreviewBanner] = useState(null);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" } }),
    [token]
  );

  const ensureAuth = () => {
    if (!apiUrl || !token) { notify("error", "Missing API URL or token."); return false; }
    return true;
  };

  const getApiError = (err, fallback = "Operation failed.") => {
    const d = err?.response?.data;
    return (typeof d?.message === "string" && d.message.trim()) ? d.message
      : (typeof err?.message === "string" && err.message.trim()) ? err.message
      : fallback;
  };

  const couponOptions = useMemo(() => {
    if (!Array.isArray(offers)) return [];
    return offers.filter((o) => o && (o.coupon_code || o.code)).map((o) => ({
      value: (o.coupon_code || o.code || "").toUpperCase(),
      label: `${o.name || "Offer"} — ${(o.coupon_code || o.code || "").toUpperCase()}`,
    }));
  }, [offers]);

  /* ── API ── */

  const fetchBanners = useCallback(async () => {
    if (!ensureAuth()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/booking-engine/promo-banners`, authHeaders);
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load banners.");
      setBanners(res.data?.data?.items || []);
    } catch (err) {
      console.error(err);
      notify("error", getApiError(err, "Failed to load banners."));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token]);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const saveBanner = async () => {
    if (!form.name?.trim()) return notify("error", "Banner name is required.");
    if (!form.headline?.trim()) return notify("error", "Headline is required.");
    if (!ensureAuth()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), headline: form.headline.trim(),
        subtext: (form.subtext || "").trim(),
        coupon_code: (form.coupon_code || "").trim().toUpperCase(),
        cta_text: (form.cta_text || "").trim(),
        cta_url: (form.cta_url || "").trim(),
        template: form.template || "gradient-bold",
        bg_color_1: form.bg_color_1 || "#667eea",
        bg_color_2: form.bg_color_2 || "#764ba2",
        text_color: form.text_color || "#ffffff",
        accent_color: form.accent_color || "#fbbf24",
        width: parseInt(form.width) || 400,
        height: parseInt(form.height) || 400,
        size_preset: form.size_preset || "Custom",
        is_active: !!form.is_active,
        show_on_frontend: !!form.show_on_frontend,
        start_at: moment(form.start_at).startOf("day").toISOString(),
        end_at: moment(form.end_at).endOf("day").toISOString(),
        position: form.position || "hero-overlay",
        custom_css: form.custom_css || "",
        sort_order: parseInt(form.sort_order) || 0,
      };
      const url = form.id ? `${apiUrl}/booking-engine/promo-banners/${form.id}` : `${apiUrl}/booking-engine/promo-banners`;
      const res = form.id ? await axios.put(url, payload, authHeaders) : await axios.post(url, payload, authHeaders);
      if (res?.data?.success === false) throw new Error(res.data?.message || "Save failed.");
      notify("success", form.id ? "Banner updated." : "Banner created.");
      setDrawerOpen(false);
      fetchBanners();
    } catch (err) { console.error(err); notify("error", getApiError(err, "Save failed.")); }
    finally { setSaving(false); }
  };

  const deleteBanner = (banner) => {
    if (!ensureAuth()) return;
    Modal.confirm({
      title: "Delete this banner?",
      content: `"${banner.name || "Banner"}" will be permanently removed.`,
      okText: "Delete", okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await axios.delete(`${apiUrl}/booking-engine/promo-banners/${banner.id}`, authHeaders);
          if (!res.data?.success) throw new Error(res.data?.message || "Delete failed.");
          notify("success", "Banner deleted."); fetchBanners();
        } catch (err) { console.error(err); notify("error", getApiError(err, "Delete failed.")); }
      },
    });
  };

  const toggleField = async (id, field, value) => {
    if (!ensureAuth()) return;
    try {
      await axios.patch(`${apiUrl}/booking-engine/promo-banners/${id}/toggle`, { field, value }, authHeaders);
      setBanners((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
    } catch (err) { notify("error", getApiError(err, "Toggle failed.")); }
  };

  const duplicateBanner = async (banner) => {
    if (!ensureAuth()) return;
    try {
      const payload = { ...banner, name: `${banner.name} (Copy)`, is_active: false };
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.companyid;
      await axios.post(`${apiUrl}/booking-engine/promo-banners`, payload, authHeaders);
      notify("success", "Banner duplicated."); fetchBanners();
    } catch (err) { notify("error", getApiError(err, "Duplicate failed.")); }
  };

  /* ── Drawer helpers ── */

  const openCreate = () => {
    setForm({ ...DEFAULT_FORM });
    setDateRange({ startDate: moment(DEFAULT_FORM.start_at), endDate: moment(DEFAULT_FORM.end_at) });
    setDrawerOpen(true);
  };

  const openEdit = (banner) => {
    setForm({
      ...DEFAULT_FORM, ...banner,
      start_at: banner.start_at ? moment(banner.start_at).format("YYYY-MM-DD") : DEFAULT_FORM.start_at,
      end_at: banner.end_at ? moment(banner.end_at).format("YYYY-MM-DD") : DEFAULT_FORM.end_at,
    });
    setDateRange({
      startDate: moment(banner.start_at || DEFAULT_FORM.start_at),
      endDate: moment(banner.end_at || DEFAULT_FORM.end_at),
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => { setDrawerOpen(false); setSaving(false); };

  const handleSizePreset = (label) => {
    const p = SIZE_PRESETS.find((s) => s.label === label);
    if (!p) return;
    setForm((f) => ({ ...f, size_preset: label, ...(p.width > 0 ? { width: p.width, height: p.height } : {}) }));
  };

  const getBannerStatus = (b) => {
    if (!b.is_active) return { color: "default", text: "Inactive", icon: <CloseCircleFilled /> };
    const now = moment();
    if (now.isBefore(moment(b.start_at))) return { color: "blue", text: "Scheduled", icon: <ClockCircleOutlined /> };
    if (now.isAfter(moment(b.end_at))) return { color: "red", text: "Expired", icon: <CloseCircleFilled /> };
    return { color: "green", text: "Live", icon: <CheckCircleFilled /> };
  };

  const getPreviewScale = () => {
    const maxW = 480;
    return (form.width || 400) > maxW ? maxW / (form.width || 400) : 1;
  };

  const renderValidityPicker = () => (
    <DateRangePicker
      initialSettings={{
        singleDatePicker: false, showDropdowns: true, autoApply: true, opens: "left",
        startDate: dateRange.startDate.toDate(), endDate: dateRange.endDate.toDate(),
        locale: { format: "DD MMM YYYY" },
        ranges: {
          "Next 7 Days": [moment().toDate(), moment().add(6, "days").toDate()],
          "Next 30 Days": [moment().toDate(), moment().add(29, "days").toDate()],
          "Next 90 Days": [moment().toDate(), moment().add(89, "days").toDate()],
        },
      }}
      onCallback={(start, end) => {
        const s = moment(start).startOf("day"); const e = moment(end).endOf("day");
        setDateRange({ startDate: s, endDate: e });
        setForm((f) => ({ ...f, start_at: s.format("YYYY-MM-DD"), end_at: e.format("YYYY-MM-DD") }));
      }}
    >
      <div className="input-group">
        <input type="text" className="form-control" readOnly
          value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`}
          style={{ background: "#fff", fontWeight: 700, textAlign: "center", cursor: "pointer", height: 40, borderRadius: 10 }} />
        <span className="input-group-text bg-white" style={{ borderRadius: "0 10px 10px 0" }}><i className="fa fa-calendar" /></span>
      </div>
    </DateRangePicker>
  );

  /* ── Table columns for listing ── */

  const columns = [
    {
      title: "Banner",
      key: "preview",
      width: 140,
      render: (_, b) => {
        const s = Math.min(1, 110 / (b.width || 400));
        return (
          <div style={{ width: 120, height: 80, overflow: "hidden", borderRadius: 10, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            onClick={() => { setPreviewBanner(b); setPreviewOpen(true); }}>
            <div style={{ transform: `scale(${s})`, transformOrigin: "center" }}>{renderTemplate(b, 1)}</div>
          </div>
        );
      },
    },
    {
      title: "Details",
      key: "details",
      render: (_, b) => (
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{b.name || "Untitled"}</div>
          <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>{b.headline || "—"}</div>
          {b.coupon_code && <Tag color="purple" style={{ marginTop: 4 }}>{b.coupon_code}</Tag>}
        </div>
      ),
    },
    {
      title: "Template",
      key: "template",
      width: 140,
      render: (_, b) => <Tag>{TEMPLATES.find((t) => t.id === b.template)?.name || b.template}</Tag>,
    },
    {
      title: "Size",
      key: "size",
      width: 100,
      render: (_, b) => <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>{b.width}×{b.height}</span>,
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 180,
      render: (_, b) => (
        <div style={{ fontSize: 12 }}>
          <div><b>{b.start_at ? moment(b.start_at).format("DD MMM YYYY") : "—"}</b></div>
          <div style={{ color: "#6b7280" }}>to <b>{b.end_at ? moment(b.end_at).format("DD MMM YYYY") : "—"}</b></div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, b) => {
        const s = getBannerStatus(b);
        return <Tag icon={s.icon} color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: "Visible",
      key: "visible",
      width: 80,
      render: (_, b) => (
        <Switch size="small" checked={!!b.show_on_frontend} onChange={(c) => toggleField(b.id, "show_on_frontend", c)} />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 160,
      render: (_, b) => (
        <div className="d-flex gap-1 justify-content-end">
          <Tooltip title="Preview"><Button size="small" icon={<EyeOutlined />} onClick={() => { setPreviewBanner(b); setPreviewOpen(true); }} /></Tooltip>
          <Tooltip title="Duplicate"><Button size="small" icon={<CopyOutlined />} onClick={() => duplicateBanner(b)} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(b)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteBanner(b)} /></Tooltip>
        </div>
      ),
    },
  ];

  /* ════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════ */

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><Spin size="large" /></div>;

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        {/* Header */}
        <div className="page-header page-top-icons" style={{ marginBottom: 16 }}>
          <div className="row align-items-center">
            <div className="col-md-6">
              <h3 className="page-title" style={{ marginBottom: 0 }}>
                <PictureOutlined className="me-2" />
                Promotional Banners
              </h3>
              <div style={{ color: "#6b7280", fontWeight: 600, marginTop: 4 }}>
                Create, schedule, and manage promotional banners for your booking engine.
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-end">
                <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreate}>
                  Create Banner
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table listing */}
        <Card className="shadow-lg border-0" style={{ borderRadius: 16, overflow: "hidden", border: "1px solid rgba(15,23,42,0.08)" }}>
          {banners.length === 0 ? (
            <div style={{ padding: 40 }}>
              <Empty
                image={<PictureOutlined style={{ fontSize: 48, color: "#d1d5db" }} />}
                description={
                  <div>
                    <div style={{ fontWeight: 800 }}>No banners yet</div>
                    <div style={{ color: "#6b7280" }}>Create your first promotional banner to boost direct bookings.</div>
                  </div>
                }
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Create Banner</Button>
              </Empty>
            </div>
          ) : (
            <Table columns={columns} dataSource={banners} rowKey={(r) => r.id} pagination={{ pageSize: 10 }} style={{ padding: 0 }} />
          )}
        </Card>

        {/* ═══════════  RIGHT DRAWER — FULL WIDTH, NO LEFT HERO  ═══════════ */}
        <Drawer
          open={drawerOpen}
          onClose={closeDrawer}
          width={1140}
          zIndex={9999}
          closeIcon={null}
          styles={{
            header: { display: "none" },
            body: { padding: 0 },
            mask: { zIndex: 9998 },
            content: { zIndex: 9999 },
            wrapper: { zIndex: 9999 },
          }}
          maskStyle={{ zIndex: 9998 }}
          placement="right"
          destroyOnClose
          maskClosable={!saving}
        >
          <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#fff" }}>
            {/* Top bar */}
            <div style={{
              padding: "18px 28px", borderBottom: "1px solid rgba(15,23,42,0.08)",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              background: "linear-gradient(90deg, #f8fafc, #fff)",
            }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                  {form.id ? "Edit Banner" : "Create New Banner"}
                </div>
                <div style={{ color: "#6b7280", fontWeight: 600, marginTop: 2 }}>
                  Design your banner, set colors, schedule, and go live.
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: "#6b7280", fontWeight: 800, fontSize: 13 }}>Active</span>
                  <Switch checked={!!form.is_active} onChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: "#6b7280", fontWeight: 800, fontSize: 13 }}>Frontend</span>
                  <Switch checked={!!form.show_on_frontend} onChange={(v) => setForm((p) => ({ ...p, show_on_frontend: v }))} />
                </div>
                <Button onClick={closeDrawer} disabled={saving}>Close</Button>
              </div>
            </div>

            {/* Content: form left + preview right */}
            <div style={{ flex: 1, overflowY: "auto", display: "flex" }}>
              {/* LEFT — Form */}
              <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>
                {/* Template selector */}
                <label style={S.label}>Template</label>
                <div className="d-flex gap-3 mb-4">
                  {TEMPLATES.map((t) => (
                    <div key={t.id} onClick={() => setForm((p) => ({ ...p, template: t.id }))} style={{
                      flex: 1, padding: "14px 16px", borderRadius: 14,
                      border: `2px solid ${form.template === t.id ? "#3c0469" : "#e5e7eb"}`,
                      background: form.template === t.id ? "rgba(60,4,105,0.04)" : "#fafafa",
                      cursor: "pointer", transition: "all .2s",
                    }}>
                      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{t.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Name + Coupon */}
                <Row gutter={16}>
                  <Col span={12}>
                    <label style={S.label}>Banner Name</label>
                    <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Diwali Sale" size="large" />
                  </Col>
                  <Col span={12}>
                    <label style={S.label}>Coupon Code <span style={{ color: "#9ca3af", fontWeight: 500 }}>(optional)</span></label>
                    {couponOptions.length > 0 ? (
                      <Select showSearch value={form.coupon_code || undefined} onChange={(v) => setForm((p) => ({ ...p, coupon_code: v }))} placeholder="Link an offer coupon" style={{ width: "100%" }} size="large" allowClear options={couponOptions} optionFilterProp="label" />
                    ) : (
                      <Input value={form.coupon_code} onChange={(e) => setForm((p) => ({ ...p, coupon_code: e.target.value.toUpperCase() }))} placeholder="e.g. FESTIVE10" size="large" />
                    )}
                  </Col>
                </Row>

                {/* Headline + Subtext */}
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <label style={S.label}>Headline</label>
                    <Input value={form.headline} onChange={(e) => setForm((p) => ({ ...p, headline: e.target.value }))} placeholder="e.g. Avail 10% Discount on Festival!" size="large" />
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col span={24}>
                    <label style={S.label}>Subtext</label>
                    <Input.TextArea value={form.subtext} onChange={(e) => setForm((p) => ({ ...p, subtext: e.target.value }))} placeholder="Book now and save big on your next stay" rows={2} />
                  </Col>
                </Row>

                {/* CTA */}
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={8}>
                    <label style={S.label}>CTA Button Text</label>
                    <Input value={form.cta_text} onChange={(e) => setForm((p) => ({ ...p, cta_text: e.target.value }))} placeholder="Book Now" size="large" />
                  </Col>
                  <Col span={16}>
                    <label style={S.label}>CTA Link <span style={{ color: "#9ca3af", fontWeight: 500 }}>(optional)</span></label>
                    <Input value={form.cta_url} onChange={(e) => setForm((p) => ({ ...p, cta_url: e.target.value }))} placeholder="https://..." size="large" />
                  </Col>
                </Row>

                {/* Colors — react-input-color */}
                <Card style={S.sectionCard} title={<span style={S.sectionTitle}>🎨 Banner Colors</span>}>
                  <div className="d-flex gap-4 flex-wrap">
                    <ColorField label="Primary BG" value={form.bg_color_1} onChange={(v) => setForm((p) => ({ ...p, bg_color_1: v }))} />
                    <ColorField label="Secondary BG" value={form.bg_color_2} onChange={(v) => setForm((p) => ({ ...p, bg_color_2: v }))} />
                    <ColorField label="Text" value={form.text_color} onChange={(v) => setForm((p) => ({ ...p, text_color: v }))} />
                    <ColorField label="Accent / Badge" value={form.accent_color} onChange={(v) => setForm((p) => ({ ...p, accent_color: v }))} />
                  </div>
                </Card>

                {/* Size & Schedule */}
                <Card style={S.sectionCard} title={<span style={S.sectionTitle}>📐 Size & Schedule</span>}>
                  <Row gutter={16}>
                    <Col span={10}>
                      <label style={S.label}>Size Preset</label>
                      <Select value={form.size_preset} onChange={handleSizePreset} options={SIZE_PRESETS.map((s) => ({ value: s.label, label: s.label }))} style={{ width: "100%" }} size="large" />
                    </Col>
                    <Col span={7}>
                      <label style={S.label}>Width (px)</label>
                      <Input type="number" value={form.width} onChange={(e) => setForm((p) => ({ ...p, width: parseInt(e.target.value) || 400 }))} size="large" min={100} />
                    </Col>
                    <Col span={7}>
                      <label style={S.label}>Height (px)</label>
                      <Input type="number" value={form.height} onChange={(e) => setForm((p) => ({ ...p, height: parseInt(e.target.value) || 400 }))} size="large" min={50} />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={14}>
                      <label style={S.label}>Schedule (Show From → Until)</label>
                      {renderValidityPicker()}
                    </Col>
                    <Col span={10}>
                      <label style={S.label}>Display Position</label>
                      <Select value={form.position} onChange={(v) => setForm((p) => ({ ...p, position: v }))} options={POSITIONS} style={{ width: "100%" }} size="large" />
                    </Col>
                  </Row>
                </Card>
              </div>

              {/* RIGHT — Live Preview */}
              <div style={{
                width: 480, flexShrink: 0, background: "#f1f5f9",
                borderLeft: "1px solid rgba(15,23,42,0.06)",
                display: "flex", flexDirection: "column", alignItems: "center",
                padding: "24px 20px",
                position: "sticky", top: 0, height: "calc(100vh - 130px)",
              }}>
                <div style={{
                  fontWeight: 900, fontSize: 12, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "#374151", marginBottom: 20,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <EyeOutlined /> Live Preview
                </div>

                <div style={{
                  flex: 1, width: "100%",
                  background: "repeating-conic-gradient(#dde1e7 0% 25%, transparent 0% 50%) 50% / 20px 20px",
                  borderRadius: 16, padding: 20,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "auto",
                }}>
                  {renderTemplate(form, getPreviewScale())}
                </div>

                <div style={{ marginTop: 14, fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
                  {form.width} × {form.height}px &nbsp;·&nbsp; {TEMPLATES.find((t) => t.id === form.template)?.name || "Template"}
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div style={{
              padding: "14px 28px", borderTop: "1px solid rgba(15,23,42,0.08)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#fff",
            }}>
              <div style={{ color: "#6b7280", fontWeight: 700 }}>
                {form.id ? "Editing existing banner" : "Creating a new banner"}
              </div>
              <div className="d-flex gap-2">
                <Button size="large" onClick={closeDrawer} disabled={saving}>Cancel</Button>
                <Button size="large" type="primary" onClick={saveBanner} loading={saving}>
                  {form.id ? "Update Banner" : "Create Banner"}
                </Button>
              </div>
            </div>
          </div>
        </Drawer>

        {/* Full-size preview modal */}
        <Modal
          open={previewOpen}
          onCancel={() => setPreviewOpen(false)}
          footer={null}
          width={Math.min((previewBanner?.width || 400) + 100, window.innerWidth - 60)}
          centered destroyOnClose
          styles={{ body: { display: "flex", justifyContent: "center", padding: 32, background: "#f1f5f9", borderRadius: 12 } }}
        >
          {previewBanner && renderTemplate(previewBanner, 1)}
        </Modal>

        <style>{`
          .ant-drawer-mask { background: rgba(15,23,42,0.45) !important; }
          .ant-drawer-content { border-radius: 16px 0 0 16px; overflow: hidden; }
          .ant-table-thead > tr > th { font-weight: 900; color: #0f172a; }
        `}</style>
      </div>
    </div>
  );
};

const S = {
  label: {
    fontWeight: 800, fontSize: 12, letterSpacing: "0.04em",
    textTransform: "uppercase", color: "#111827",
    display: "block", marginBottom: 6,
  },
  sectionCard: {
    marginTop: 20, borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  },
  sectionTitle: { fontWeight: 900, color: "#0f172a" },
};

export default PromoBannerManager;